import React, { useState, useEffect, useContext } from 'react';
import { 
  Form, 
  Row, 
  Col, 
  Button, 
  Card, 
  Alert, 
  Badge,
  InputGroup 
} from 'react-bootstrap';
import { DataContext } from '../../init/getData';
import Loader from '../../components/loader/loader';
import toast from 'react-hot-toast';
import { 
  FaUser, 
  FaSave, 
  FaKey, 
  FaEye, 
  FaEyeSlash, 
  FaTimes,
  FaBuilding,
  FaCalendarAlt,
  FaUserTag,
  FaShieldAlt
} from "react-icons/fa";

function UserProfile() {
  const { userData, api } = useContext(DataContext);
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    userName: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Fetch user profile
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/user/profile');
      if (response.data.status) {
        setProfile(response.data.data);
        setFormData({
          name: response.data.data.name || '',
          userName: response.data.data.userName || '',
          password: '',
          confirmPassword: ''
        });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to load profile');
      console.error('Profile fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Check for password match in real-time (optional, no validation)
    if ((name === 'password' || name === 'confirmPassword') && 
        formData.password && formData.confirmPassword) {
      if (name === 'password') {
        setErrors(prev => ({
          ...prev,
          confirmPassword: value !== formData.confirmPassword ? 'Passwords do not match' : ''
        }));
      } else if (name === 'confirmPassword') {
        setErrors(prev => ({
          ...prev,
          confirmPassword: value !== formData.password ? 'Passwords do not match' : ''
        }));
      }
    }
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate name field
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Only check if passwords match (no strength validation)
    if (formData.password && formData.confirmPassword && 
        formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // If confirm password has value but password is empty
    if (formData.confirmPassword && !formData.password) {
      newErrors.confirmPassword = 'Please enter a password first';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    
    // Validate form (only name and password match)
    const newErrors = validateForm();
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fix the errors before submitting');
      return;
    }
    
    setIsLoading(true);
    try {
      const payload = {
        user: {
          name: formData.name.trim()
        }
      };
      
      // Only include password if provided
      if (formData.password.trim()) {
        payload.user.password = formData.password;
      }
      
      const response = await api.post('/user/update-profile', payload);
      
      if (response.data.status) {
        toast.success('Profile updated successfully!');
        setEditMode(false);
        setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
        setErrors({});
        setTouched({});
        setShowPassword(false);
        setShowConfirmPassword(false);
        fetchProfile();
      } else {
        toast.error(response.data.message || 'Update failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
      console.error('Update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    // Reset form data to current profile
    if (profile) {
      setFormData({
        name: profile.name || '',
        userName: profile.userName || '',
        password: '',
        confirmPassword: ''
      });
    }
    setErrors({});
    setTouched({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading && !profile) {
    return (
      <div className="container mt-4">
        <Loader />
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <Row className="justify-content-center">
        <Col xl={8} lg={10}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-0 py-4">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0 d-flex align-items-center">
                    <FaUser className="me-3 text-primary" size={28} />
                    User Profile
                  </h3>
                  <p className="text-muted mb-0 mt-1">
                    Manage your personal information and password
                  </p>
                </Col>
                <Col xs="auto">
                  {!editMode ? (
                    <Button 
                      variant="primary"
                      onClick={() => setEditMode(true)}
                      className="px-4"
                    >
                      <FaUser className="me-2" />
                      Edit Profile
                    </Button>
                  ) : (
                    <Button 
                      variant="outline-secondary"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      <FaTimes className="me-2" />
                      Cancel
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Header>
            
            <Card.Body className="p-4">
              {!profile ? (
                <Alert variant="warning">
                  Unable to load profile data. Please try again.
                </Alert>
              ) : (
                <>
                  <Form onSubmit={handleSubmit}>
                    {/* Personal Information Section */}
                    <Card className="mb-4 border">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">Personal Information</h5>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                Full Name *
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                onBlur={() => handleBlur('name')}
                                disabled={!editMode}
                                isInvalid={!!errors.name && touched.name}
                                placeholder="Enter your full name"
                                className={editMode ? 'border-primary' : ''}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.name}
                              </Form.Control.Feedback>
                              {!editMode && !formData.name && (
                                <Form.Text className="text-muted">
                                  Not set
                                </Form.Text>
                              )}
                            </Form.Group>
                          </Col>
                          
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                Username
                              </Form.Label>
                              <InputGroup>
                                <InputGroup.Text>
                                  <FaUserTag />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={formData.userName}
                                  disabled
                                  readOnly
                                  className="bg-light"
                                />
                              </InputGroup>
                              <Form.Text className="text-muted">
                                Username cannot be changed
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    {/* Password Change Section */}
                    <Card className="mb-4 border">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0 d-flex align-items-center">
                          <FaKey className="me-2" />
                          Change Password
                          <Badge bg="light" text="dark" className="ms-2">
                            Optional
                          </Badge>
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Alert variant="info" className="mb-4">
                          <FaShieldAlt className="me-2" />
                          Leave password fields blank to keep your current password. You can use any password.
                        </Alert>
                        
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                New Password
                              </Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type={showPassword ? "text" : "password"}
                                  name="password"
                                  value={formData.password}
                                  onChange={handleInputChange}
                                  onBlur={() => handleBlur('password')}
                                  disabled={!editMode}
                                  isInvalid={!!errors.password && touched.password}
                                  placeholder={editMode ? "Enter any password" : ""}
                                  className={editMode ? 'border-primary' : ''}
                                />
                                {editMode && (
                                  <Button
                                    variant="outline-secondary"
                                    onClick={togglePasswordVisibility}
                                  >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                  </Button>
                                )}
                                <Form.Control.Feedback type="invalid">
                                  {errors.password}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>
                          
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                Confirm Password
                              </Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type={showConfirmPassword ? "text" : "password"}
                                  name="confirmPassword"
                                  value={formData.confirmPassword}
                                  onChange={handleInputChange}
                                  onBlur={() => handleBlur('confirmPassword')}
                                  disabled={!editMode}
                                  isInvalid={!!errors.confirmPassword && touched.confirmPassword}
                                  placeholder={editMode ? "Re-enter password" : ""}
                                  className={editMode ? 'border-primary' : ''}
                                />
                                {editMode && (
                                  <Button
                                    variant="outline-secondary"
                                    onClick={toggleConfirmPasswordVisibility}
                                  >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                  </Button>
                                )}
                                <Form.Control.Feedback type="invalid">
                                  {errors.confirmPassword}
                                </Form.Control.Feedback>
                              </InputGroup>
                              {formData.password && !errors.confirmPassword && formData.confirmPassword && (
                                <Form.Text className="text-success">
                                  ✓ Passwords match
                                </Form.Text>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    {/* Action Buttons */}
                    {editMode && (
                      <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                        <Button 
                          variant="outline-secondary" 
                          onClick={handleCancel}
                          disabled={isLoading}
                          className="px-4"
                        >
                          Cancel
                        </Button>
                        <Button 
                          variant="primary" 
                          type="submit"
                          disabled={isLoading}
                          className="px-4"
                        >
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <FaSave className="me-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </Form>

                  {/* Profile Information (Display only) */}
                  {!editMode && profile && (
                    <Card className="mt-4 border">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">Profile Information</h5>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <div className="mb-4">
                              <h6 className="text-muted mb-2">
                                <FaUser className="me-2" />
                                Personal Details
                              </h6>
                              <div className="ps-3">
                                <div className="mb-3">
                                  <strong>Full Name:</strong>
                                  <p className="mb-0">{profile.name || 'Not set'}</p>
                                </div>
                                <div className="mb-3">
                                  <strong>Username:</strong>
                                  <p className="mb-0">{profile.userName || 'Not set'}</p>
                                </div>
                                <div className="mb-3">
                                  <strong>Role:</strong>
                                  <p className="mb-0">
                                    <Badge bg="info">
                                      {profile.role?.name || 'Not set'}
                                    </Badge>
                                  </p>
                                </div>
                              </div>
                            </div>
                          </Col>
                          
                          <Col md={6}>
                            <div className="mb-4">
                              <h6 className="text-muted mb-2">
                                <FaBuilding className="me-2" />
                                Company Information
                              </h6>
                              <div className="ps-3">
                                <div className="mb-3">
                                  <strong>Company:</strong>
                                  <p className="mb-0">{profile.company?.name || 'Not set'}</p>
                                </div>
                              </div>
                            </div>
                            
                            <div>
                              <h6 className="text-muted mb-2">
                                <FaCalendarAlt className="me-2" />
                                Account Timeline
                              </h6>
                              <div className="ps-3">
                                <div className="mb-3">
                                  <strong>Member Since:</strong>
                                  <p className="mb-0">{formatDate(profile.createdAt)}</p>
                                </div>
                                {profile.updatedAt && (
                                  <div className="mb-3">
                                    <strong>Last Updated:</strong>
                                    <p className="mb-0">{formatDate(profile.updatedAt)}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default UserProfile;